/**
 * 관리자 보너스 지급 패널
 *
 * 왜 필요한가
 * -----------
 * 지갑의 전송 기능은 막아뒀는데, 현장에서 관리자가 고객에게 보너스를 줘야 한다.
 * 그래서 어드민이 권한을 준 회원에게만 이 화면이 보이고, 고객 QR 을 찍어서 보낸다.
 *
 * 안전장치
 *  · 1회 한도와 하루 한도는 서버가 강제한다(화면 계산은 안내용일 뿐이다).
 *  · QR 을 읽은 뒤 바로 보내지 않는다. 받는 사람 이름·코드를 먼저 보여주고 확인받는다.
 *  · 카메라가 막힌 환경(일부 지갑 인앱 브라우저)에서는 직접 입력으로 빠진다.
 */
import { useState, useRef, useEffect } from 'react';
import { Camera, X, Send, Gift, AlertTriangle, Keyboard } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  authHeaders: () => Record<string, string>;
}

export function BonusPanel({ authHeaders }: Props) {
  const [perm, setPerm] = useState<any>(null);
  const [sends, setSends] = useState<any[]>([]);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState(false);
  const [recipientRaw, setRecipientRaw] = useState('');
  const [recipient, setRecipient] = useState<any>(null);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef<any>(null);

  const load = async () => {
    try {
      const [p, h] = await Promise.all([
        fetch('/api/bonus/my-permission', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/bonus/my-sends', { headers: authHeaders() }).then(r => r.json()),
      ]);
      if (p.success) setPerm(p);
      if (h.success) setSends(h.sends || []);
    } catch { /* 조회 실패가 화면을 막지 않는다 */ }
  };
  useEffect(() => { load(); }, []);

  // 카메라 정리 — 화면을 떠날 때 반드시 꺼야 한다
  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch { /* 이미 꺼짐 */ }
    try { scannerRef.current?.clear(); } catch { /* noop */ }
    scannerRef.current = null;
    setScanning(false);
  };
  useEffect(() => () => { stopScan(); }, []);

  const startScan = async () => {
    setScanning(true);
    setRecipient(null);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const el = 'bonus-qr-reader';
      const s = new Html5Qrcode(el);
      scannerRef.current = s;
      await s.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (text: string) => { await stopScan(); await resolve(text); },
        () => { /* 인식 실패 프레임은 무시 */ },
      );
    } catch (e: any) {
      await stopScan();
      setManual(true);
      toast.error('카메라를 쓸 수 없습니다. 코드를 직접 입력해주세요.');
    }
  };

  /** 받는 사람 확인 — 바로 보내지 않고 누구인지 먼저 보여준다 */
  const resolve = async (value: string) => {
    setRecipientRaw(value);
    try {
      const d = await fetch('/api/bonus/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ value }),
      }).then(r => r.json());
      if (!d.success) { toast.error(d.error || '조회 실패'); return; }
      setRecipient(d.recipient);
    } catch (e: any) { toast.error(e?.message || '조회 실패'); }
  };

  /** 금액 버튼 — 누를 때마다 더한다. 현장에서 10+10+50 처럼 빠르게 쌓기 위함이다. */
  const addAmount = (n: number) => {
    const cur = Number(amount) || 0;
    const next = cur + n;
    const cap = Math.min(Number(perm?.perTxLimit ?? Infinity), Number(perm?.remainingToday ?? Infinity));
    setAmount(String(Math.min(next, cap)));
  };
  const stepAmount = (d: number) => {
    const cur = Number(amount) || 0;
    const cap = Math.min(Number(perm?.perTxLimit ?? Infinity), Number(perm?.remainingToday ?? Infinity));
    setAmount(String(Math.max(0, Math.min(cur + d, cap))));
  };

  const send = async () => {
    const amt = Number(amount);
    if (!recipient || !Number.isFinite(amt) || amt <= 0) return;

    const lock = perm?.lock?.enabled && perm?.lock?.cliffMonths > 0;
    const msg = `받는 사람: ${recipient.name || '(회원 아님)'}${recipient.code ? ' / ' + recipient.code : ''}\n`
      + `지갑: ${recipient.address}\n\n`
      + `보낼 수량: ${amt.toLocaleString()} wHANDY\n`
      + (lock
          ? `락업: ${perm.lock.cliffMonths}개월 거치 · ${perm.lock.splitCount}회 분할\n`
          : `락업: 없음\n`)
      + `\n보내면 되돌릴 수 없습니다. 진행할까요?`;
    if (!window.confirm(msg)) return;

    setBusy(true);
    try {
      const d = await fetch('/api/bonus/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ recipient: recipientRaw, amount: amt, memo }),
      }).then(r => r.json());
      if (!d.success) { toast.error(d.error || '지급 실패'); return; }
      toast.success(`${amt.toLocaleString()} wHANDY 지급 완료`);
      setRecipient(null); setRecipientRaw(''); setAmount(''); setMemo('');
      load();
    } catch (e: any) { toast.error(e?.message || '지급 실패'); }
    finally { setBusy(false); }
  };

  if (!perm) return <div className="p-6 text-center text-muted-foreground">불러오는 중…</div>;
  if (!perm.allowed) {
    return (
      <div className="p-6 text-center">
        <Gift className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
        <p className="text-muted-foreground">보너스 지급 권한이 없습니다.</p>
      </div>
    );
  }

  const remain = Number(perm.remainingToday || 0);
  const lock = perm?.lock?.enabled && perm?.lock?.cliffMonths > 0;

  return (
    <div className="p-4 space-y-4">
      {/* 한도 */}
      <div className="rounded-xl bg-muted p-4">
        <p className="text-sm text-muted-foreground">오늘 남은 한도</p>
        <p className="text-3xl font-bold">
          {remain.toLocaleString()}
          <span className="text-sm font-normal text-muted-foreground ml-1">wHANDY</span>
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          1회 최대 {Number(perm.perTxLimit).toLocaleString()} · 하루 {Number(perm.dailyLimit).toLocaleString()}
          {' · '}오늘 {Number(perm.usedToday).toLocaleString()} 사용
        </p>
        {lock && (
          <p className="text-xs text-amber-700 mt-1.5">
            받는 분은 {perm.lock.cliffMonths}개월 거치 후 매월{' '}
            {Math.round(100 / perm.lock.splitCount)}%씩 {perm.lock.splitCount}회에 걸쳐 쓸 수 있습니다.
          </p>
        )}
      </div>

      {/* 스캔 */}
      {!recipient && (
        <>
          {!scanning && !manual && (
            <div className="space-y-2">
              <button onClick={startScan}
                className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2"
                style={{ background: '#FF6B00' }}>
                <Camera className="w-5 h-5" /> 고객 QR 스캔하기
              </button>
              <button onClick={() => setManual(true)}
                className="w-full py-3 rounded-xl border border-border text-sm flex items-center justify-center gap-2">
                <Keyboard className="w-4 h-4" /> 추천코드·지갑주소 직접 입력
              </button>
            </div>
          )}

          {scanning && (
            <div className="rounded-xl overflow-hidden bg-black relative">
              <div id="bonus-qr-reader" className="w-full" />
              <button onClick={stopScan}
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-2">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {manual && (
            <div className="space-y-2">
              <input
                value={recipientRaw}
                onChange={e => setRecipientRaw(e.target.value)}
                placeholder="추천코드 또는 0x 지갑주소"
                className="w-full px-4 py-4 rounded-xl text-lg outline-none border-2 border-border bg-white text-black"
              />
              <div className="flex gap-2">
                <button onClick={() => resolve(recipientRaw)} disabled={!recipientRaw.trim()}
                  className="flex-1 py-3 rounded-xl font-bold text-white disabled:opacity-40"
                  style={{ background: '#FF6B00' }}>
                  확인
                </button>
                <button onClick={() => { setManual(false); setRecipientRaw(''); }}
                  className="px-4 py-3 rounded-xl border border-border">취소</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* 받는 사람 확인 + 수량 */}
      {recipient && (
        <div className="rounded-xl border border-border p-4 space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">받는 사람</p>
            <p className="font-bold text-lg">
              {recipient.name || '(회원 아님)'}
              {recipient.code && <span className="text-sm font-normal text-muted-foreground ml-1.5">{recipient.code}</span>}
            </p>
            <p className="text-[11px] font-mono text-muted-foreground break-all">{recipient.address}</p>
            {!recipient.isMember && (
              <p className="text-xs mt-1 flex items-center gap-1" style={{ color: '#B45309' }}>
                <AlertTriangle className="w-3 h-3" /> 가입 회원이 아닙니다. 주소를 꼭 확인하세요.
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block">보낼 수량 (wHANDY)</label>

            {/* 자주 쓰는 금액 — 누를 때마다 더해진다 */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[10, 20, 50, 100].map(n => (
                <button key={n} onClick={() => addAmount(n)}
                  className="py-3 rounded-xl font-bold text-base border-2 active:scale-95 transition-transform"
                  style={{ borderColor: '#FF6B00', color: '#FF6B00', background: '#FFF7ED' }}>
                  +{n}
                </button>
              ))}
            </div>

            {/* 직접 입력 + 미세 조정 */}
            <div className="flex items-stretch gap-2">
              <button onClick={() => stepAmount(-10)}
                className="w-14 rounded-xl border-2 border-border font-bold text-2xl active:scale-95 transition-transform">
                −
              </button>
              <input
                value={amount}
                onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                inputMode="decimal"
                placeholder="0"
                className="flex-1 px-4 py-4 rounded-xl text-center text-3xl font-bold outline-none border-2 bg-white text-black"
                style={{ borderColor: Number(amount) > 0 ? '#FF6B00' : '#D4D4D8' }}
              />
              <button onClick={() => stepAmount(10)}
                className="w-14 rounded-xl border-2 border-border font-bold text-2xl active:scale-95 transition-transform">
                +
              </button>
            </div>

            <div className="flex items-center justify-between mt-2">
              <button onClick={() => setAmount('')}
                className="text-sm text-muted-foreground underline">
                지우기
              </button>
              <button onClick={() => setAmount(String(Math.min(Number(perm.perTxLimit), remain)))}
                className="text-sm font-semibold" style={{ color: '#FF6B00' }}>
                최대 {Math.min(Number(perm.perTxLimit), remain).toLocaleString()}
              </button>
            </div>

            {Number(amount) > remain && (
              <p className="text-sm text-red-500 mt-1.5 font-semibold">
                오늘 남은 한도({remain.toLocaleString()})를 넘었습니다.
              </p>
            )}
            {Number(amount) > Number(perm.perTxLimit) && (
              <p className="text-sm text-red-500 mt-1.5 font-semibold">
                1회 한도({Number(perm.perTxLimit).toLocaleString()})를 넘었습니다.
              </p>
            )}
          </div>

          <input value={memo} onChange={e => setMemo(e.target.value)} placeholder="메모 (선택)"
            className="w-full px-4 py-3 rounded-xl text-base outline-none border-2 border-border bg-white text-black" />

          <button
            onClick={send}
            disabled={busy || !(Number(amount) > 0) || Number(amount) > remain || Number(amount) > Number(perm.perTxLimit)}
            className="w-full py-5 rounded-2xl font-bold text-xl text-white disabled:opacity-40 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{ background: '#FF6B00' }}>
            <Send className="w-6 h-6" />
            {busy ? '전송 중…' : `${Number(amount) > 0 ? Number(amount).toLocaleString() + ' ' : ''}보너스 보내기`}
          </button>
          <button onClick={() => { setRecipient(null); setRecipientRaw(''); setAmount(''); }}
            className="w-full py-3 rounded-xl border-2 border-border font-semibold">
            다른 사람에게 보내기
          </button>
        </div>
      )}

      {/* 내가 보낸 내역 */}
      <div>
        <p className="text-sm font-bold mb-2">내가 보낸 내역</p>
        {sends.length === 0 && <p className="text-sm text-muted-foreground py-3">아직 없습니다.</p>}
        <div className="divide-y divide-border">
          {sends.map(s => (
            <div key={s.id} className="py-2.5 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold">
                  {s.full_name || s.nickname || '(회원 아님)'}
                  {s.referral_code && <span className="text-xs font-normal text-muted-foreground ml-1.5">{s.referral_code}</span>}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {String(s.created_at).slice(0, 16).replace('T', ' ')}
                  {s.memo && ` · ${s.memo}`}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold">{Number(s.amount).toLocaleString()}</p>
                <p className={`text-[11px] ${s.status === 'completed' ? 'text-green-600' : s.status === 'failed' ? 'text-red-500' : 'text-muted-foreground'}`}>
                  {s.status === 'completed' ? '완료' : s.status === 'failed' ? '실패' : '처리중'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
